import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { useCountry } from '../contexts/CountryContext';
import {
  Search, MapPin, BadgeCheck, ShieldCheck, Star, MessageCircle, Phone, Loader2,
  Building2, Scale, FileText, ArrowRight, Users, Award,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = [
  { code: 'UK', flag: '🇬🇧', label: 'United Kingdom' },
  { code: 'IN', flag: '🇮🇳', label: 'India' },
  { code: 'CA', flag: '🇨🇦', label: 'Canada' },
  { code: 'US', flag: '🇺🇸', label: 'United States' },
];

const TYPE_ICON = {
  agent: Building2,
  solicitor: Scale, real_estate_lawyer: Scale, attorney: Scale, advocate: Scale, notary: Scale,
  rics_surveyor: FileText, appraiser: FileText, valuer: FileText,
  mortgage_broker: Award, ca: Award,
  architect: Building2,
  estate_agent: Building2, rera_agent: Building2, realtor: Building2,
};

const FindProfessionalPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country: userCountry } = useCountry();

  const [country, setCountry] = useState(searchParams.get('country') || userCountry || 'UK');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');

  const [filters, setFilters] = useState(null);
  const [agents, setAgents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [fellBack, setFellBack] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userCountry && !searchParams.get('country')) setCountry(userCountry);
  }, [userCountry, searchParams]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [filtersRes, agentsRes, profsRes] = await Promise.all([
        axios.get(`${API_URL}/api/directory/filters`, { params: { country } }),
        (typeFilter === 'agent' || typeFilter === 'all')
          ? axios.get(`${API_URL}/api/directory/agents`, { params: { country, city: city || undefined, limit: 12 } })
          : Promise.resolve({ data: { items: [], count: 0 } }),
        (typeFilter !== 'agent')
          ? axios.get(`${API_URL}/api/directory/professionals`, {
              params: { country, city: city || undefined, type: (typeFilter !== 'all' ? typeFilter : undefined), limit: 12 },
            })
          : Promise.resolve({ data: { items: [], count: 0 } }),
      ]);
      setFilters(filtersRes.data);
      setAgents(agentsRes.data.items || []);
      setProfessionals(profsRes.data.items || []);
      setFellBack(!!(agentsRes.data?.fell_back_country_wide || profsRes.data?.fell_back_country_wide));
    } catch (err) {
      console.error('Directory fetch failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [country, city, typeFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Sync URL params for shareability
  useEffect(() => {
    const next = {};
    if (country) next.country = country;
    if (city) next.city = city;
    if (typeFilter && typeFilter !== 'all') next.type = typeFilter;
    setSearchParams(next, { replace: true });
  }, [country, city, typeFilter, setSearchParams]);

  const showAgents = typeFilter === 'all' || typeFilter === 'agent';
  const showPros = typeFilter !== 'agent';

  // Memoise derived chip list (fixes render-hot-path perf warning)
  const activeProTypes = useMemo(
    () => (filters?.professional_types || []).filter(t => t.count > 0),
    [filters]
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      {/* Hero */}
      <section className="bg-[#0F172A] text-white border-b border-[#E07A3A]/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-3">Find a Professional</p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl leading-[1.1] max-w-3xl">
            Verified agents, solicitors, surveyors & more — <span className="italic text-[#E07A3A]">all in one place.</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg mt-4 max-w-2xl">
            Every professional on Shopperden is <b>ID-verified</b> and has their licence checked against their statutory body. Filter by country, city and service.
          </p>

          {/* Search bar */}
          <div className="mt-8 bg-white p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-3" data-testid="directory-search">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="bg-white border-slate-200 text-[#0F172A] h-12" data-testid="country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.flag} {c.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (e.g. London, Mumbai)"
              className="bg-white border-slate-200 text-[#0F172A] h-12" data-testid="city-input" />

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="bg-white border-slate-200 text-[#0F172A] h-12" data-testid="type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All professionals</SelectItem>
                <SelectItem value="agent">Estate / Letting Agents</SelectItem>
                {filters?.professional_types?.map(t => (
                  <SelectItem key={t.key} value={t.key}>
                    {t.label}{t.count > 0 ? ` (${t.count})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={fetchAll} className="bg-[#E07A3A] hover:bg-[#C4652E] text-white h-12 px-6" data-testid="directory-search-btn">
              <Search className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Search</span>
            </Button>
          </div>

          {filters && (
            <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/70">
              <button type="button" onClick={() => setTypeFilter('agent')}
                className={`px-3 py-1 border ${typeFilter === 'agent' ? 'bg-[#E07A3A] border-[#E07A3A] text-white' : 'border-white/20 hover:border-white/40'}`}
                data-testid="chip-agents">
                <Users className="h-3 w-3 inline mr-1" /> Agents ({filters.agents.count})
              </button>
              {activeProTypes.map(t => (
                <button key={t.key} type="button" onClick={() => setTypeFilter(t.key)}
                  className={`px-3 py-1 border ${typeFilter === t.key ? 'bg-[#E07A3A] border-[#E07A3A] text-white' : 'border-white/20 hover:border-white/40'}`}>
                  {t.body_short} ({t.count})
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#E07A3A] mx-auto" /></div>
        ) : (
          <>
            {fellBack && city && (
              <div
                className="mb-8 bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900"
                data-testid="directory-fellback-banner"
              >
                <b>No exact matches for "{city}".</b> Showing top verified pros across {country === 'IN' ? 'India' : country === 'CA' ? 'Canada' : country === 'UK' ? 'the United Kingdom' : country} instead.
              </div>
            )}
            {/* Verified Agents Leaderboard */}
            {showAgents && (
              <div className="mb-12" data-testid="agents-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5 border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A]">
                      {city ? `Verified agents in ${city}` : `Top verified agents${country ? ` in ${country}` : ''}`}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Ranked by reviews, response time and active listings.</p>
                  </div>
                  {agents.length >= 6 && (
                    <Link to={`/find-a-professional?country=${country}&type=agent`} className="text-sm text-[#E07A3A] hover:underline font-semibold">
                      View all agents →
                    </Link>
                  )}
                </div>

                {agents.length === 0 ? (
                  <EmptyState title="No verified agents here yet" subtitle="Try a different city or country — or ask us to invite an agent." />
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agents.map((a, idx) => <AgentCard key={a.id} agent={a} rank={idx + 1} />)}
                  </div>
                )}
              </div>
            )}

            {/* Verified Professionals */}
            {showPros && (
              <div data-testid="pros-section">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5 border-b border-slate-100 pb-3">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A]">Verified professionals</h2>
                    <p className="text-xs text-slate-500 mt-1">Solicitors, surveyors, mortgage brokers, architects, chartered accountants — every licence verified.</p>
                  </div>
                </div>

                {professionals.length === 0 ? (
                  <EmptyState
                    title={typeFilter === 'all' ? 'No verified professionals here yet' : `No verified ${typeFilter.replace('_', ' ')}s here yet`}
                    subtitle="As KYP verifications are approved, they'll appear here. Check back soon."
                    cta={{ href: '/professional/onboard', label: 'Are you a professional? Join Shopperden →' }}
                  />
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {professionals.map(p => <ProfessionalCard key={p.id} pro={p} />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Trust banner */}
        <div className="mt-12 bg-white border border-slate-200 p-6 flex items-start gap-4" data-testid="trust-banner">
          <ShieldCheck className="h-6 w-6 text-[#E07A3A] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-600">
            <p className="font-semibold text-[#0F172A] mb-1">How verification works</p>
            <p>Every agent completes ID checks and professional-body verification (NAEA/ARLA in the UK, RERA in India, RECO in Canada). Professionals additionally sign an NDA and indemnity under our{' '}
              <Link to="/legal/professional-policy" className="text-[#E07A3A] underline">Professional Onboarding Policy</Link>. Shopperden is the venue — the service contract is always directly between you and the professional.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

// --- Sub-components ---

const RANK_MEDALS = ['🥇', '🥈', '🥉'];

const AgentCard = ({ agent, rank }) => (
  <div className="luxury-card p-5 group hover:shadow-md transition-shadow" data-testid={`agent-card-${agent.id}`}>
    <div className="flex items-start gap-3">
      <div className="w-14 h-14 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0 relative">
        {agent.avatar ? (
          <img src={agent.avatar} alt={agent.name} className="w-14 h-14 object-cover" />
        ) : (
          <span className="text-[#E07A3A] font-semibold text-lg">{(agent.name || '?').slice(0, 2).toUpperCase()}</span>
        )}
        {rank <= 3 && <span className="absolute -top-2 -left-2 text-xl" title={`Rank ${rank}`}>{RANK_MEDALS[rank - 1]}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-semibold text-[#0F172A] truncate">{agent.name}</p>
          {agent.verified_badge && <BadgeCheck className="h-4 w-4 text-[#E07A3A]" title="Verified" />}
        </div>
        <p className="text-xs text-slate-500 truncate">{agent.city || agent.country} · {agent.years_experience ? `${agent.years_experience} yrs exp` : 'Estate agent'}</p>
      </div>
    </div>

    {agent.bio && <p className="text-xs text-slate-600 mt-3 leading-relaxed line-clamp-3">{agent.bio}</p>}

    <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
      {agent.review_avg > 0 && (
        <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-500 fill-amber-500" /> {agent.review_avg.toFixed(1)} · {agent.review_count}</span>
      )}
      {agent.response_time_hours != null && (
        <span>⚡ Responds in {agent.response_time_hours < 1 ? '<1h' : `${agent.response_time_hours}h`}</span>
      )}
      {agent.listings_active > 0 && (
        <span>{agent.listings_active} active</span>
      )}
    </div>

    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
      <Link to={`/agent/${agent.id}`} className="flex-1 bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs font-semibold px-3 py-2 text-center" data-testid={`view-agent-${agent.id}`}>
        View profile <ArrowRight className="h-3 w-3 inline ml-1" />
      </Link>
      <Link to={`/agent/${agent.id}?contact=1`} className="bg-[#FEF3EC] border border-[#E07A3A]/30 hover:bg-[#E07A3A]/10 px-3 py-2" title="Message">
        <MessageCircle className="h-4 w-4 text-[#E07A3A]" />
      </Link>
    </div>
  </div>
);

const ProfessionalCard = ({ pro }) => {
  const Icon = TYPE_ICON[pro.professional_type] || Award;
  const firstFee = pro.fee_schedule?.[0];
  return (
    <div className="luxury-card p-5 hover:shadow-md transition-shadow" data-testid={`pro-card-${pro.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-semibold text-[#0F172A] truncate">{pro.name}</p>
            {pro.verified && <BadgeCheck className="h-4 w-4 text-[#E07A3A]" title="KYP-verified" />}
          </div>
          <p className="text-xs text-slate-500 truncate">{pro.professional_type?.replace('_', ' ')} · {pro.license_body}</p>
          {pro.license_state && <p className="text-[10px] text-slate-400 mt-0.5">{pro.license_state}</p>}
        </div>
      </div>

      {pro.bio && <p className="text-xs text-slate-600 mt-3 leading-relaxed line-clamp-3">{pro.bio}</p>}

      <div className="mt-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="h-3 w-3 text-green-600" /> Licence: {pro.license_number_masked}
        </span>
      </div>

      {firstFee && (
        <p className="mt-2 text-xs text-[#0F172A]">From <b>{firstFee.currency} {firstFee.fee}</b> — {firstFee.service}</p>
      )}

      <Link to={`/professionals/${pro.id}`} className="mt-4 flex items-center justify-between px-3 py-2 bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs font-semibold" data-testid={`view-pro-${pro.id}`}>
        View & enquire <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
};

const EmptyState = ({ title, subtitle, cta }) => (
  <div className="border border-dashed border-slate-200 bg-white p-12 text-center">
    <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-3" />
    <p className="font-semibold text-[#0F172A] text-base">{title}</p>
    <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{subtitle}</p>
    {cta && <Link to={cta.href} className="inline-block mt-4 text-sm text-[#E07A3A] hover:underline font-semibold">{cta.label}</Link>}
  </div>
);

export default FindProfessionalPage;
